import {
	Box,
	Button,
	Flex,
	HStack,
	Heading,
	Icon,
	Text,
	VStack,
} from "@chakra-ui/react";
import { Link } from "react-router-dom";
import { BiPhone, BiPlusCircle } from "react-icons/bi";

import { useLangContext } from "configs/languageContext";
import { translation } from "data/translations";
import { PageHeader } from "components/layout/header";
import { PageFooter } from "components/layout/footer";

// Warmer, softer palette — moves away from stark black + green combo.
const C = {
	primary: "#0F766E",        // deeper teal — more refined than the bright emerald
	primaryDark: "#115E59",
	primarySoft: "#CCFBF1",
	primarySoftWash: "#F0FDFA",
	accent: "#EA580C",         // warm orange — urgency / rescue feel
	accentSoft: "#FFEDD5",
	emergency: "#DC2626",
	emergencySoft: "#FEE2E2",
	ink: "#1E293B",            // slate-800 (not pure black)
	muted: "#475569",
	mutedLight: "#94A3B8",
	bg: "#FFFBF5",             // warm cream
	bgAlt: "#FEF8EE",
	border: "#E7E2D6",
};

const HomePage = () => {
	const { lang } = useLangContext();

	return (
		<Flex direction="column" minH="100vh" bg="white">
			<PageHeader />

			<Box position="relative" overflow="hidden" bg={C.bg} flex="1" display="flex" alignItems="center">
				{/* warm-toned decorative washes */}
				<Box position="absolute" top="-200px" right="-160px" w="540px" h="540px"
					bg={C.primarySoft} opacity={0.5} filter="blur(90px)" borderRadius="full" pointerEvents="none" />
				<Box position="absolute" bottom="-200px" left="-120px" w="460px" h="460px"
					bg={C.accentSoft} opacity={0.55} filter="blur(110px)" borderRadius="full" pointerEvents="none" />

				<Box position="relative" maxW="900px" mx="auto" px={{ base: 4, md: 8 }} py={{ base: 6, md: 14 }} w="100%">
					<VStack spacing={{ base: 4, md: 7 }} align="center" textAlign="center">

						{/* Brand badge — restrained, no big pixelated raster */}
						<HStack
							spacing={2}
							bg="white"
							borderWidth="1px"
							borderColor={C.border}
							borderRadius="full"
							px={{ base: 3, md: 4 }}
							py={1.5}
							boxShadow="sm"
						>
							<Box w="6px" h="6px" borderRadius="full" bg={C.primary} />
							<Text fontSize={{ base: "2xs", md: "xs" }} fontWeight="700" color={C.ink} letterSpacing={{ base: "0.12em", md: "0.18em" }} textTransform="uppercase">
								SalveMonos · Guanacaste
							</Text>
						</HStack>

						{/* Title */}
						<Heading
							as="h1"
							fontSize={{ base: "3xl", sm: "4xl", md: "6xl", lg: "7xl" }}
							fontWeight="800"
							letterSpacing="-0.035em"
							lineHeight={{ base: "1.05", md: "0.98" }}
							color={C.ink}
							maxW="780px"
						>
							{translation.homepage_head_rescue[lang]}
						</Heading>

						{/* Promise — warm + reassuring */}
						<HStack
							bg="white"
							borderWidth="1px"
							borderColor={C.primarySoft}
							borderRadius="full"
							px={{ base: 4, md: 5 }} py={{ base: 1.5, md: 2 }}
							boxShadow={`0 4px 14px ${C.primarySoftWash}`}
						>
							<Box w="8px" h="8px" borderRadius="full" bg={C.primary} />
							<Text fontSize={{ base: "sm", md: "lg" }} fontWeight="700" color={C.primary} letterSpacing="-0.005em">
								{translation.homepage_promise[lang]}
							</Text>
						</HStack>

						{/* Sub copy */}
						<Text fontSize={{ base: "sm", md: "md" }} color={C.muted} maxW="560px" lineHeight="1.55" px={2}>
							{translation.homepage_text1[lang]}
						</Text>

						{/* BIG primary CTA */}
						<Button
							as={Link}
							to="/form"
							target="_blank"
							w="100%"
							maxW="640px"
							h={{ base: "72px", md: "114px" }}
							fontSize={{ base: "xl", md: "3xl" }}
							fontWeight="800"
							letterSpacing="-0.015em"
							leftIcon={<Icon as={BiPlusCircle} boxSize={{ base: 6, md: 8 }} />}
							bg={C.primary}
							color="white"
							_hover={{ bg: C.primaryDark, transform: "translateY(-2px)", boxShadow: "2xl" }}
							_active={{ transform: "translateY(0)" }}
							borderRadius="xl"
							boxShadow={`0 14px 40px ${C.primarySoft}, 0 6px 16px rgba(15,118,110,0.2)`}
							mt={1}
							whiteSpace="normal"
						>
							{translation.homepage_report[lang]}
						</Button>

						{/* Response time */}
						<Text fontSize={{ base: "xs", md: "sm" }} color={C.muted} maxW="600px" lineHeight="1.55" fontStyle="italic" px={2}>
							{translation.homepage_response[lang]}
						</Text>

						{/* Emergency phone */}
						<Box
							as="a"
							href="tel:+50684029264"
							w="100%"
							maxW="640px"
							bg="white"
							borderWidth="2px"
							borderColor={C.emergencySoft}
							borderRadius="xl"
							px={{ base: 4, md: 6 }} py={{ base: 4, md: 5 }}
							display="block"
							transition="all 0.2s"
							_hover={{ borderColor: C.emergency, transform: "translateY(-1px)", boxShadow: "lg" }}
						>
							<HStack spacing={{ base: 3, md: 4 }} justify="center">
								<Flex w={{ base: "40px", md: "48px" }} h={{ base: "40px", md: "48px" }} align="center" justify="center"
									bg={C.emergencySoft} color={C.emergency} borderRadius="lg" flexShrink={0}>
									<BiPhone size="22" />
								</Flex>
								<VStack spacing={0} align="flex-start">
									<Text fontSize={{ base: "2xs", md: "xs" }} fontWeight="700" color={C.emergency} textTransform="uppercase" letterSpacing="0.1em">
										{lang === "es" ? "Seguimiento" : "Follow up"}
									</Text>
									<Text fontSize={{ base: "lg", md: "2xl" }} fontWeight="800" color={C.ink} letterSpacing="-0.01em">
										+506 8402 9264
									</Text>
								</VStack>
							</HStack>
						</Box>

						{/* Staff link — slightly elevated chip */}
						<Box pt={2}>
							<Box
								as={Link} to="/login"
								fontSize="sm" fontWeight="600"
								color={C.muted}
								borderWidth="1px"
								borderColor={C.border}
								borderRadius="full"
								px={5} py={2}
								bg="white"
								boxShadow="0 1px 2px rgba(15,23,42,0.04)"
								transition="all .15s ease"
								_hover={{
									color: C.primary,
									borderColor: C.primarySoft,
									bg: C.primarySoftWash,
									transform: "translateY(-1px)",
								}}
							>
								{translation.homepage_login[lang]}
							</Box>
						</Box>
					</VStack>
				</Box>
			</Box>

			<PageFooter />
		</Flex>
	);
};

export default HomePage;
